import { Injectable } from '@angular/core';
import * as XLSX from 'xlsx';
import { FilterState, ChartDataset, InventoryProjectionRow } from '../_interfaces/analytics.interfaces';
import { getBreakdownDisplayName } from '../_utils/analytics-formatters.util';

export interface SelectOption {
  value: string | number;
  label: string;
  [key: string]: any;
}

export interface DrinkVolumeExportData {
  labels: string[];
  data: number[];
  compareData?: number[];
  retailRevenueData?: number[];
  compareRetailRevenueData?: number[];
  retailRevenueHeader?: string;
  compareRetailRevenueHeader?: string;
  firstColumnHeader: string;
  secondColumnHeader: string;
  thirdColumnHeader?: string;
  total: number;
  compareTotal?: number;
  retailRevenueTotal?: number;
  compareRetailRevenueTotal?: number;
}

export interface ConsumptionExportData {
  labels: string[];
  data: ChartDataset[];
  compareData?: number[];
  isStacked: boolean;
  breakdownBy: string;
  total: number;
  compareTotal?: number;
}

export interface ForecastExportData {
  projectedConsumption: {
    labels: string[];
    data: ChartDataset[];
    isStacked: boolean;
    breakdownBy: string;
    total: number;
  };
  inventoryProjection: {
    table: InventoryProjectionRow[];
    currentTotal: number;
    endTotal: number;
  };
  projectedConsumptionOverTime: {
    labels: string[];
    datasets: ChartDataset[];
    totals: number[];
  };
}

export interface StockoutsExportData {
  rows: Array<{
    machine: string;
    product: string;
    inventory: number;
    daysLeft: number;
    risk: string;
  }>;
}

@Injectable({
  providedIn: 'root'
})
export class AnalyticsExportService {

  /**
   * Export drink volume table to Excel
   */
  exportDrinkVolumeTable(
    data: DrinkVolumeExportData,
    filters: FilterState,
    tabName: string = 'Drink Volume',
    drinkOptions?: SelectOption[],
    productOptions?: SelectOption[],
    distributorOptions?: SelectOption[],
    marketOptions?: SelectOption[],
    clientOptions?: SelectOption[],
    campusOptions?: SelectOption[],
    machineOptions?: SelectOption[]
  ): void {
    console.log('[Export Service] exportDrinkVolumeTable() called');
    console.log('[Export Service] Input data:', {
      labelsCount: data.labels.length,
      dataCount: data.data.length,
      hasCompareData: !!data.compareData,
      compareDataCount: data.compareData ? data.compareData.length : 0,
      firstColumnHeader: data.firstColumnHeader,
      secondColumnHeader: data.secondColumnHeader,
      thirdColumnHeader: data.thirdColumnHeader,
      total: data.total,
      compareTotal: data.compareTotal,
      retailRevenueTotal: data.retailRevenueTotal,
      compareRetailRevenueTotal: data.compareRetailRevenueTotal,
      breakdownBy: filters.breakdownBy
    });

    const workbook = XLSX.utils.book_new();

    // Create data sheet
    const sheetData: any[][] = [];
    
    // Headers - use exact same headers as displayed in the table
    // First column: breakdown name (e.g., "Drink", "Distributor", "Client", "Campus", "Machine")
    // Second column: "Total" or "Average per Day/Week/Month" depending on averageEnabled
    // Third column (if compare enabled): "Previous Total" or "Previous Average per Day/Week/Month"
    const headers: string[] = [
      data.firstColumnHeader || getBreakdownDisplayName(filters.breakdownBy),
      data.secondColumnHeader || 'Total'
    ];
    if (data.thirdColumnHeader) {
      headers.push(data.thirdColumnHeader);
    }
    if (data.retailRevenueData) {
      headers.push(data.retailRevenueHeader || 'Retail Revenue');
    }
    if (data.compareRetailRevenueData) {
      headers.push(data.compareRetailRevenueHeader || 'Previous Retail Revenue');
    }
    sheetData.push(headers);
    console.log('[Export Service] Headers created:', headers);

    // Data rows
    console.log('[Export Service] Creating data rows, count:', data.labels.length);
    for (let i = 0; i < data.labels.length; i++) {
      const row: any[] = [data.labels[i], data.data[i]];
      if (data.compareData && data.compareData[i] !== undefined) {
        row.push(data.compareData[i]);
      }
      if (data.retailRevenueData) {
        row.push(data.retailRevenueData[i] || 0);
      }
      if (data.compareRetailRevenueData) {
        row.push(data.compareRetailRevenueData[i] || 0);
      }
      sheetData.push(row);
    }
    console.log('[Export Service] Data rows created, total rows:', sheetData.length);

    // Totals row
    const totalsRow: any[] = ['Total', data.total];
    if (data.compareTotal !== undefined) {
      totalsRow.push(data.compareTotal);
    }
    if (data.retailRevenueTotal !== undefined) {
      totalsRow.push(data.retailRevenueTotal);
    }
    if (data.compareRetailRevenueTotal !== undefined) {
      totalsRow.push(data.compareRetailRevenueTotal);
    }
    sheetData.push(totalsRow);
    console.log('[Export Service] Totals row added:', totalsRow);

    const ws = XLSX.utils.aoa_to_sheet(sheetData);
    XLSX.utils.book_append_sheet(workbook, ws, tabName);
    console.log('[Export Service] Data sheet created and added to workbook');

    // Add metadata sheet
    this.addMetadataSheet(
      workbook, 
      'drink-volume', 
      filters, 
      undefined, 
      drinkOptions, 
      productOptions,
      distributorOptions,
      marketOptions,
      clientOptions,
      campusOptions,
      machineOptions
    );
    console.log('[Export Service] Metadata sheet added');

    // Generate filename and save
    const fileName = this.generateFileName(
      'Drink_Volume', 
      filters.breakdownBy, 
      filters,
      distributorOptions,
      marketOptions,
      clientOptions,
      campusOptions,
      machineOptions
    );
    console.log('[Export Service] Generated filename:', fileName);
    console.log('[Export Service] Attempting to save file...');
    XLSX.writeFile(workbook, fileName);
    console.log('[Export Service] File saved successfully');
  }

  /**
   * Export consumption table to Excel
   */
  exportConsumptionTable(
    data: ConsumptionExportData,
    filters: FilterState,
    tabName: string = 'Consumption',
    drinkOptions?: SelectOption[],
    productOptions?: SelectOption[],
    distributorOptions?: SelectOption[],
    marketOptions?: SelectOption[],
    clientOptions?: SelectOption[],
    campusOptions?: SelectOption[],
    machineOptions?: SelectOption[]
  ): void {
    const workbook = XLSX.utils.book_new();

    // Create data sheet
    const sheetData: any[][] = [];
    
    // Headers
    const breakdownName = data.breakdownBy !== 'product' 
      ? getBreakdownDisplayName(data.breakdownBy) 
      : 'Product';
    const headers: string[] = [breakdownName];

    if (data.isStacked) {
      // Stacked format: one column per product
      data.data.forEach(dataset => {
        headers.push(dataset.label || '');
      });
    } else {
      // Simple format: Current (lbs) and optionally Previous (lbs)
      headers.push('Current (lbs)');
      if (data.compareData && data.compareData.length > 0) {
        headers.push('Previous (lbs)');
      }
    }
    sheetData.push(headers);

    // Data rows
    const labelCount = data.labels.length;
    for (let i = 0; i < labelCount; i++) {
      const row: any[] = [data.labels[i]];
      
      if (data.isStacked) {
        // Stacked: one value per product dataset
        data.data.forEach(dataset => {
          const value = dataset.data[i] || 0;
          row.push(value);
        });
      } else {
        // Simple: current value and optionally compare value
        const currentValue = (data.data[0] && data.data[0].data) ? data.data[0].data[i] || 0 : 0;
        row.push(currentValue);
        if (data.compareData && data.compareData[i] !== undefined) {
          row.push(data.compareData[i]);
        }
      }
      sheetData.push(row);
    }

    // Totals row
    const totalsRow: any[] = ['Total'];
    if (data.isStacked) {
      data.data.forEach(dataset => {
        const total = dataset.data.reduce((sum, val) => sum + (val || 0), 0);
        totalsRow.push(total);
      });
    } else {
      totalsRow.push(data.total);
      if (data.compareTotal !== undefined) {
        totalsRow.push(data.compareTotal);
      }
    }
    sheetData.push(totalsRow);

    const ws = XLSX.utils.aoa_to_sheet(sheetData);
    XLSX.utils.book_append_sheet(workbook, ws, tabName);

    // Add metadata sheet
    this.addMetadataSheet(
      workbook, 
      'consumption', 
      filters, 
      undefined, 
      drinkOptions, 
      productOptions,
      distributorOptions,
      marketOptions,
      clientOptions,
      campusOptions,
      machineOptions
    );

    // Generate filename and save
    const fileName = this.generateFileName(
      'Consumption', 
      filters.breakdownBy, 
      filters,
      distributorOptions,
      marketOptions,
      clientOptions,
      campusOptions,
      machineOptions
    );
    XLSX.writeFile(workbook, fileName);
  }

  /**
   * Export forecast tables to Excel (all 3 tables as separate sheets)
   */
  exportForecastTable(
    data: ForecastExportData,
    filters: FilterState,
    forecastDays: number,
    drinkOptions?: SelectOption[],
    productOptions?: SelectOption[],
    distributorOptions?: SelectOption[],
    marketOptions?: SelectOption[],
    clientOptions?: SelectOption[],
    campusOptions?: SelectOption[],
    machineOptions?: SelectOption[]
  ): void {
    const workbook = XLSX.utils.book_new();

    // Sheet 1: Projected Consumption by Product
    const projectedSheetData: any[][] = [];
    const breakdownName = data.projectedConsumption.breakdownBy !== 'product'
      ? getBreakdownDisplayName(data.projectedConsumption.breakdownBy)
      : 'Product';
    
    const projectedHeaders: string[] = [breakdownName];
    
    if (data.projectedConsumption.isStacked) {
      data.projectedConsumption.data.forEach(dataset => {
        projectedHeaders.push(dataset.label || '');
      });
    } else {
      projectedHeaders.push('Projected (lbs)');
    }
    projectedSheetData.push(projectedHeaders);

    // Data rows
    for (let i = 0; i < data.projectedConsumption.labels.length; i++) {
      const row: any[] = [data.projectedConsumption.labels[i]];
      
      if (data.projectedConsumption.isStacked) {
        data.projectedConsumption.data.forEach(dataset => {
          const value = dataset.data[i] || 0;
          row.push(value);
        });
      } else {
        const value = (data.projectedConsumption.data[0] && data.projectedConsumption.data[0].data) 
          ? data.projectedConsumption.data[0].data[i] || 0 
          : 0;
        row.push(value);
      }
      projectedSheetData.push(row);
    }

    // Totals row
    const projectedTotalsRow: any[] = ['Total'];
    if (data.projectedConsumption.isStacked) {
      data.projectedConsumption.data.forEach(dataset => {
        const total = dataset.data.reduce((sum, val) => sum + (val || 0), 0);
        projectedTotalsRow.push(total);
      });
    } else {
      projectedTotalsRow.push(data.projectedConsumption.total);
    }
    projectedSheetData.push(projectedTotalsRow);

    const projectedWs = XLSX.utils.aoa_to_sheet(projectedSheetData);
    XLSX.utils.book_append_sheet(workbook, projectedWs, 'Projected Consumption');

    // Sheet 2: Inventory Projection Summary
    const inventorySheetData: any[][] = [
      ['Product', 'Current (lbs)', 'Projected End (lbs)']
    ];

    data.inventoryProjection.table.forEach(row => {
      inventorySheetData.push([
        row.product,
        row.current,
        row.projectedEnd
      ]);
    });

    inventorySheetData.push([
      'Total',
      data.inventoryProjection.currentTotal,
      data.inventoryProjection.endTotal
    ]);

    const inventoryWs = XLSX.utils.aoa_to_sheet(inventorySheetData);
    XLSX.utils.book_append_sheet(workbook, inventoryWs, 'Inventory Projection Summary');

    // Sheet 3: Projected Consumption Over Time
    const timeSeriesSheetData: any[][] = [];
    const timeSeriesHeaders: string[] = ['Date'];
    data.projectedConsumptionOverTime.datasets.forEach(dataset => {
      timeSeriesHeaders.push(dataset.label || '');
    });
    timeSeriesSheetData.push(timeSeriesHeaders);

    // Data rows
    for (let i = 0; i < data.projectedConsumptionOverTime.labels.length; i++) {
      const row: any[] = [data.projectedConsumptionOverTime.labels[i]];
      data.projectedConsumptionOverTime.datasets.forEach(dataset => {
        const value = dataset.data[i] || 0;
        row.push(value);
      });
      timeSeriesSheetData.push(row);
    }

    // Totals row
    const timeSeriesTotalsRow: any[] = ['Total'];
    data.projectedConsumptionOverTime.totals.forEach(total => {
      timeSeriesTotalsRow.push(total);
    });
    timeSeriesSheetData.push(timeSeriesTotalsRow);

    const timeSeriesWs = XLSX.utils.aoa_to_sheet(timeSeriesSheetData);
    XLSX.utils.book_append_sheet(workbook, timeSeriesWs, 'Projected Consumption Over Time');

    // Add metadata sheet
    this.addMetadataSheet(
      workbook, 
      'forecast', 
      filters, 
      forecastDays, 
      drinkOptions, 
      productOptions,
      distributorOptions,
      marketOptions,
      clientOptions,
      campusOptions,
      machineOptions
    );

    // Generate filename and save
    const fileName = this.generateFileName(
      'Forecast', 
      filters.breakdownBy, 
      filters,
      distributorOptions,
      marketOptions,
      clientOptions,
      campusOptions,
      machineOptions
    );
    XLSX.writeFile(workbook, fileName);
  }

  /**
   * Export stockouts table to Excel
   */
  exportStockoutsTable(
    data: StockoutsExportData,
    filters: FilterState,
    tabName: string = 'Stock-outs',
    drinkOptions?: SelectOption[],
    productOptions?: SelectOption[],
    distributorOptions?: SelectOption[],
    marketOptions?: SelectOption[],
    clientOptions?: SelectOption[],
    campusOptions?: SelectOption[],
    machineOptions?: SelectOption[]
  ): void {
    const workbook = XLSX.utils.book_new();

    // Create data sheet
    const sheetData: any[][] = [
      ['Machine', 'Product', 'Inventory (lbs)', 'Days Until Empty', 'Risk']
    ];

    // Data rows
    data.rows.forEach(row => {
      sheetData.push([
        row.machine,
        row.product,
        row.inventory,
        row.daysLeft,
        row.risk
      ]);
    });

    const ws = XLSX.utils.aoa_to_sheet(sheetData);
    XLSX.utils.book_append_sheet(workbook, ws, tabName);

    // Add metadata sheet
    this.addMetadataSheet(
      workbook, 
      'stockouts', 
      filters, 
      undefined, 
      drinkOptions, 
      productOptions,
      distributorOptions,
      marketOptions,
      clientOptions,
      campusOptions,
      machineOptions
    );

    // Generate filename and save
    const fileName = this.generateFileName(
      'Stockouts', 
      'product', 
      filters,
      distributorOptions,
      marketOptions,
      clientOptions,
      campusOptions,
      machineOptions
    );
    XLSX.writeFile(workbook, fileName);
  }

  /**
   * Generate filename based on tab, breakdown, and filters
   */
  private generateFileName(
    tabName: string, 
    breakdown: string, 
    filters: FilterState,
    distributorOptions?: SelectOption[],
    marketOptions?: SelectOption[],
    clientOptions?: SelectOption[],
    campusOptions?: SelectOption[],
    machineOptions?: SelectOption[]
  ): string {
    const breakdownDisplay = getBreakdownDisplayName(breakdown);
    const mostSpecificFilter = this.getMostSpecificFilter(
      filters,
      distributorOptions,
      marketOptions,
      clientOptions,
      campusOptions,
      machineOptions
    );
    const dateStr = new Date().toISOString().split('T')[0];

    let fileName = `${tabName}_${breakdownDisplay}`;
    
    if (mostSpecificFilter) {
      fileName += `_${mostSpecificFilter}`;
    }
    
    fileName += `_${dateStr}.xlsx`;
    
    return fileName;
  }

  /**
   * Get the most specific filter for filename (Machine > Campus > Client > Market > Distributor)
   * Returns labels instead of IDs, joined with hyphens
   */
  private getMostSpecificFilter(
    filters: FilterState,
    distributorOptions?: SelectOption[],
    marketOptions?: SelectOption[],
    clientOptions?: SelectOption[],
    campusOptions?: SelectOption[],
    machineOptions?: SelectOption[]
  ): string {
    // Check in order of specificity
    if (filters.machines && filters.machines.length > 0) {
      const labels = this.convertIdsToLabels(filters.machines, machineOptions);
      // Sanitize labels for filename (remove invalid characters)
      return labels.map(label => this.sanitizeForFilename(label)).join('-');
    }
    if (filters.campuses && filters.campuses.length > 0) {
      const labels = this.convertIdsToLabels(filters.campuses, campusOptions);
      return labels.map(label => this.sanitizeForFilename(label)).join('-');
    }
    if (filters.clients && filters.clients.length > 0) {
      const labels = this.convertIdsToLabels(filters.clients, clientOptions);
      return labels.map(label => this.sanitizeForFilename(label)).join('-');
    }
    if (filters.markets && filters.markets.length > 0) {
      const labels = this.convertIdsToLabels(filters.markets, marketOptions);
      return labels.map(label => this.sanitizeForFilename(label)).join('-');
    }
    if (filters.distributors && filters.distributors.length > 0) {
      const labels = this.convertIdsToLabels(filters.distributors, distributorOptions);
      return labels.map(label => this.sanitizeForFilename(label)).join('-');
    }
    return '';
  }

  /**
   * Sanitize a string for use in a filename
   * Removes or replaces invalid filename characters
   */
  private sanitizeForFilename(str: string): string {
    // Replace invalid filename characters with underscores
    // Invalid characters: / \ : * ? " < > |
    return str.replace(/[\/\\:*?"<>|]/g, '_').trim();
  }

  /**
   * Add metadata sheet to workbook
   */
  private addMetadataSheet(
    workbook: XLSX.WorkBook,
    tabKey: string,
    filters: FilterState,
    forecastDays?: number,
    drinkOptions?: SelectOption[],
    productOptions?: SelectOption[],
    distributorOptions?: SelectOption[],
    marketOptions?: SelectOption[],
    clientOptions?: SelectOption[],
    campusOptions?: SelectOption[],
    machineOptions?: SelectOption[]
  ): void {
    const metadata: any[][] = [
      ['Field', 'Value']
    ];

    // Tab name
    const tabNames: Record<string, string> = {
      'drink-volume': 'Drink Volume',
      'consumption': 'Consumption',
      'forecast': 'Forecast',
      'stockouts': 'Stock-outs'
    };
    metadata.push(['Tab Name', tabNames[tabKey] || tabKey]);

    // Breakdown type (skip for stockouts)
    if (tabKey !== 'stockouts') {
      metadata.push(['Breakdown Type', getBreakdownDisplayName(filters.breakdownBy)]);
    }

    // Date range (skip for stockouts)
    if (tabKey !== 'stockouts') {
      let dateRange = 'N/A';
      if (filters.startDate && filters.endDate) {
        const startDate = this.formatDate(filters.startDate);
        const endDate = this.formatDate(filters.endDate);
        dateRange = `${startDate} to ${endDate}`;
      } else if (filters.datePreset) {
        const presetLabels: Record<string, string> = {
          '7': 'Last 7 days',
          '30': 'Last 30 days',
          '90': 'Last 90 days',
          '365': 'Last year',
          'custom': 'Custom'
        };
        dateRange = presetLabels[filters.datePreset] || filters.datePreset;
      }
      metadata.push(['Date Range', dateRange]);
    }

    // Selected filters - use labels instead of IDs
    if (filters.distributors && filters.distributors.length > 0) {
      const distributorLabels = this.convertIdsToLabels(filters.distributors, distributorOptions);
      metadata.push(['Selected Distributors', distributorLabels.join(', ')]);
    }
    if (filters.markets && filters.markets.length > 0) {
      const marketLabels = this.convertIdsToLabels(filters.markets, marketOptions);
      metadata.push(['Selected Markets', marketLabels.join(', ')]);
    }
    if (filters.clients && filters.clients.length > 0) {
      const clientLabels = this.convertIdsToLabels(filters.clients, clientOptions);
      metadata.push(['Selected Clients', clientLabels.join(', ')]);
    }
    if (filters.campuses && filters.campuses.length > 0) {
      const campusLabels = this.convertIdsToLabels(filters.campuses, campusOptions);
      metadata.push(['Selected Campuses', campusLabels.join(', ')]);
    }
    if (filters.machines && filters.machines.length > 0) {
      const machineLabels = this.convertIdsToLabels(filters.machines, machineOptions);
      metadata.push(['Selected Machines', machineLabels.join(', ')]);
    }
    if (filters.products && filters.products.length > 0) {
      const productLabels = this.convertIdsToLabels(filters.products, productOptions);
      metadata.push(['Selected Products', productLabels.join(', ')]);
    }
    if (filters.drinks && filters.drinks.length > 0) {
      const drinkLabels = this.convertIdsToLabels(filters.drinks, drinkOptions);
      metadata.push(['Selected Drinks', drinkLabels.join(', ')]);
    }

    // Compare mode (skip for stockouts)
    if (tabKey !== 'stockouts') {
      metadata.push(['Compare Mode Enabled', filters.compareEnabled ? 'Yes' : 'No']);
    }

    // Average settings (if applicable - only for drink-volume and consumption)
    if (tabKey === 'drink-volume' || tabKey === 'consumption') {
      metadata.push(['Average Enabled', filters.averageEnabled ? 'Yes' : 'No']);
      if (filters.averageEnabled) {
        metadata.push(['Average By', filters.avgBy.charAt(0).toUpperCase() + filters.avgBy.slice(1)]);
      }
    }

    // Forecast days (for forecast tab)
    if (tabKey === 'forecast' && forecastDays !== undefined) {
      metadata.push(['Forecast Days', forecastDays.toString()]);
    }

    // Export timestamp
    metadata.push(['Export Timestamp', new Date().toLocaleString('en-US')]);

    const ws = XLSX.utils.aoa_to_sheet(metadata);
    XLSX.utils.book_append_sheet(workbook, ws, 'Metadata');
  }

  /**
   * Format date string for display (same format as UI)
   */
  private formatDate(dateStr: string): string {
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    } catch {
      return dateStr;
    }
  }

  /**
   * Convert filter IDs to display labels using options
   */
  private convertIdsToLabels(ids: string[], options?: SelectOption[]): string[] {
    if (!options || options.length === 0) {
      return ids; // Fallback to IDs if options not available
    }

    return ids.map(id => {
      const option = options.find(opt => String(opt.value) === String(id));
      return option ? option.label : id; // Use label if found, otherwise fallback to ID
    });
  }
}

